import { useMemo, useState } from "react";
import { Phone, Mail, MessageCircle, ShieldAlert, Copy } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { cn } from "@/lib/utils";

import { translateFr as tfr } from "@/i18n";
const DISCARD_REASONS = ["Niveau insuffisant", "Profil non pertinent", "Déjà représenté",
  "Non disponible", "Hors projet", "Autre"];
const RELATIONSHIPS = ["Père", "Mère", "Tuteur", "Tutrice", "Frère", "Sœur", "Famille",
  "Éducateur", "Coach", "Conseiller", "Autre"];

export function DiscardDialog({ ids, name, tf, onClose, onDone }) {
  const [reason, setReason] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!reason) { toast.error(tf("Un motif est requis")); return; }
    setBusy(true);
    try {
      for (const id of ids) await api.post(`/reperes/${id}/discard`, { reason, notes });
      toast.success(tf("Prospect(s) écarté(s) — restauration possible via « Voir les écartés »"));
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || tf("Erreur")); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent aria-describedby={undefined} className="sm:max-w-[380px]" data-testid="discard-dialog">
        <DialogHeader><DialogTitle className="text-base">{tf("Écarter ce prospect ?")}{ids.length > 1 ? ` (${ids.length})` : name ? ` — ${name}` : ""}</DialogTitle></DialogHeader>
        <Select value={reason} onValueChange={setReason}>
          <SelectTrigger className="h-9 text-xs" data-testid="discard-reason"><SelectValue placeholder={tf("Motif (requis)")} /></SelectTrigger>
          <SelectContent>{DISCARD_REASONS.map((r) => <SelectItem key={r} value={r}>{tf(r)}</SelectItem>)}</SelectContent>
        </Select>
        <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder={tf("Notes (optionnel)")} className="text-xs min-h-[60px]" data-testid="discard-notes" />
        <p className="text-[11px] text-muted-foreground">{tf("L'athlète n'est pas supprimé : notes, historique et interlocuteurs sont conservés.")}</p>
        <DialogFooter>
          <Button variant="outline" size="sm" onClick={onClose}>{tf("Annuler")}</Button>
          <Button size="sm" disabled={busy || !reason} className="bg-athlynx hover:bg-athlynx/90 text-white" onClick={submit} data-testid="discard-submit">{tf("Écarter")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function ObserveDialog({ p, tf, onClose, onDone }) {
  const [form, setForm] = useState({ title: "", date: "", time: "18:00", venue: "", notes: "" });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!form.date) { toast.error(tf("Date requise")); return; }
    setBusy(true);
    try {
      await api.post(`/reperes/${p.id}/observation`, {
        title: form.title || undefined,
        starts_at: `${form.date}T${form.time || "18:00"}:00`, venue: form.venue, notes: form.notes });
      toast.success(tf("Observation planifiée et ajoutée au calendrier")); onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || tf("Erreur")); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent aria-describedby={undefined} className="sm:max-w-[400px]" data-testid="observe-dialog">
        <DialogHeader><DialogTitle className="text-base">{tf("Planifier une observation")} — {p.name}</DialogTitle></DialogHeader>
        <Input value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
          placeholder={tf("Match / événement (optionnel)")} className="h-9 text-xs" data-testid="observe-title" />
        <div className="grid grid-cols-2 gap-2.5">
          <div><label className="text-[11px] text-muted-foreground" htmlFor="observe-date">{tf("Date")}</label>
            <Input id="observe-date" type="date" value={form.date} onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))} className="h-9 text-xs" data-testid="observe-date" /></div>
          <div><label className="text-[11px] text-muted-foreground" htmlFor="observe-time">{tf("Heure")}</label>
            <Input id="observe-time" type="time" value={form.time} onChange={(e) => setForm((f) => ({ ...f, time: e.target.value }))} className="h-9 text-xs" data-testid="observe-time" /></div>
        </div>
        <Input value={form.venue} onChange={(e) => setForm((f) => ({ ...f, venue: e.target.value }))} placeholder={tf("Lieu / stade")} className="h-9 text-xs" data-testid="observe-venue" />
        <Textarea value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} placeholder={tf("Points à observer…")} className="text-xs min-h-[60px]" data-testid="observe-notes" />
        <DialogFooter>
          <Button variant="outline" size="sm" onClick={onClose}>{tf("Annuler")}</Button>
          <Button size="sm" disabled={busy} className="bg-foreground hover:bg-foreground/90 text-background" onClick={submit} data-testid="observe-submit">{tf("Planifier")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function ContactDialog({ p, tf, onClose, onDone }) {
  const [form, setForm] = useState({ name: "", relationship: tfr("Père"), is_legal_representative: false, phone: "", email: "", source: "" });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!form.name.trim()) { toast.error(tf("Nom requis")); return; }
    setBusy(true);
    try {
      await api.post(`/reperes/${p.id}/contacts`, form);
      toast.success(tf("Interlocuteur ajouté")); onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || tf("Erreur")); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent aria-describedby={undefined} className="sm:max-w-[420px]" data-testid="add-contact-dialog">
        <DialogHeader><DialogTitle className="text-base">{tf("Ajouter un interlocuteur")} — {p.name}</DialogTitle></DialogHeader>
        <Input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
          placeholder={tf("Nom complet (requis)")} className="h-9 text-xs" data-testid="contact-name-input" />
        <div className="grid grid-cols-2 gap-2.5">
          <Select value={form.relationship} onValueChange={(v) => setForm((f) => ({ ...f, relationship: v }))}>
            <SelectTrigger className="h-9 text-xs" data-testid="contact-relationship-select"><SelectValue /></SelectTrigger>
            <SelectContent>{RELATIONSHIPS.map((r) => <SelectItem key={r} value={r}>{tf(r)}</SelectItem>)}</SelectContent>
          </Select>
          <label className="flex items-center gap-2 text-xs cursor-pointer select-none">
            <input type="checkbox" checked={form.is_legal_representative}
              onChange={(e) => setForm((f) => ({ ...f, is_legal_representative: e.target.checked }))}
              className="accent-[#E11D48]" data-testid="contact-legal-rep-checkbox" />
            {tf("Représentant légal")}
          </label>
        </div>
        <div className="grid grid-cols-2 gap-2.5">
          <Input value={form.phone} onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
            placeholder={tf("Téléphone")} className="h-9 text-xs" data-testid="contact-phone-input" />
          <Input value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
            placeholder="Email" className="h-9 text-xs" data-testid="contact-email-input" />
        </div>
        <Input value={form.source} onChange={(e) => setForm((f) => ({ ...f, source: e.target.value }))}
          placeholder={tf("Provenance des coordonnées (ex. : fourni par…)")} className="h-9 text-xs" data-testid="contact-source-input" />
        <DialogFooter>
          <Button variant="outline" size="sm" onClick={onClose}>{tf("Annuler")}</Button>
          <Button size="sm" disabled={busy || !form.name.trim()} className="bg-foreground hover:bg-foreground/90 text-background" onClick={submit} data-testid="contact-save-btn">{tf("Ajouter")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function SourceDialog({ p, tf, onClose, onDone }) {
  const [form, setForm] = useState({
    source_type: p.source_type || "manual",
    source_label: p.source_label || "", source_notes: p.source_notes || "" });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try { await api.patch(`/reperes/${p.id}`, form); toast.success(tf("Source mise à jour")); onDone(); }
    catch (e) { toast.error(e?.response?.data?.detail || tf("Erreur")); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent aria-describedby={undefined} className="sm:max-w-[400px]" data-testid="source-dialog">
        <DialogHeader><DialogTitle className="text-base">{tf("Pourquoi ce joueur ?")} — {p.name}</DialogTitle></DialogHeader>
        <Select value={form.source_type} onValueChange={(v) => setForm((f) => ({ ...f, source_type: v }))}>
          <SelectTrigger className="h-9 text-xs" data-testid="source-type-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="recommendation">{tf("Recommandation")}</SelectItem>
            <SelectItem value="import">{tf("Import")}</SelectItem>
            <SelectItem value="manual">{tf("Ajout manuel")}</SelectItem>
          </SelectContent>
        </Select>
        <Input value={form.source_label} onChange={(e) => setForm((f) => ({ ...f, source_label: e.target.value }))}
          placeholder={form.source_type === "recommendation" ? tf("Recommandé par…") : tf("Détail de la source")}
          className="h-9 text-xs" data-testid="source-label-input" />
        <Textarea value={form.source_notes} onChange={(e) => setForm((f) => ({ ...f, source_notes: e.target.value }))}
          placeholder={tf("Contexte (pourquoi ce joueur ?)")} className="text-xs min-h-[70px]" data-testid="source-notes-input" />
        <DialogFooter>
          <Button variant="outline" size="sm" onClick={onClose}>{tf("Annuler")}</Button>
          <Button size="sm" disabled={busy} className="bg-foreground hover:bg-foreground/90 text-background" onClick={submit} data-testid="source-save-btn">{tf("Enregistrer")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function SituationDialog({ p, tf, onClose, onDone }) {
  const [form, setForm] = useState({
    contract_end_date: p.contract_end_date || "", level: p.level || "",
    last_club: p.last_club || "", nationality: p.nationality || "" });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      await api.patch(`/reperes/${p.id}`, { ...form, contract_end_date: form.contract_end_date || null });
      toast.success(tf("Situation mise à jour")); onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || tf("Erreur")); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent aria-describedby={undefined} className="sm:max-w-[400px]" data-testid="situation-dialog">
        <DialogHeader><DialogTitle className="text-base">{tf("Situation actuelle")} — {p.name}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-2.5">
          <div><label className="text-[11px] text-muted-foreground" htmlFor="sit-contract">{tf("Fin de contrat")}</label>
            <Input id="sit-contract" type="date" value={form.contract_end_date} onChange={(e) => setForm((f) => ({ ...f, contract_end_date: e.target.value }))} className="h-9 text-xs" data-testid="situation-contract-input" /></div>
          <div><label className="text-[11px] text-muted-foreground" htmlFor="sit-level">{tf("Niveau")}</label>
            <Input id="sit-level" value={form.level} onChange={(e) => setForm((f) => ({ ...f, level: e.target.value }))} placeholder="N3, U19 National…" className="h-9 text-xs" data-testid="situation-level-input" /></div>
          <div><label className="text-[11px] text-muted-foreground" htmlFor="sit-lastclub">{tf("Dernier club")}</label>
            <Input id="sit-lastclub" value={form.last_club} onChange={(e) => setForm((f) => ({ ...f, last_club: e.target.value }))} className="h-9 text-xs" data-testid="situation-lastclub-input" /></div>
          <div><label className="text-[11px] text-muted-foreground" htmlFor="sit-nat">{tf("Nationalité")}</label>
            <Input id="sit-nat" value={form.nationality} onChange={(e) => setForm((f) => ({ ...f, nationality: e.target.value }))} className="h-9 text-xs" data-testid="situation-nationality-input" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" size="sm" onClick={onClose}>{tf("Annuler")}</Button>
          <Button size="sm" disabled={busy} className="bg-foreground hover:bg-foreground/90 text-background" onClick={submit} data-testid="situation-save-btn">{tf("Enregistrer")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function LinkDialog({ p, tf, onClose, onDone }) {
  const [form, setForm] = useState({ label: "", url: "" });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!form.url.trim()) { toast.error(tf("URL requise")); return; }
    setBusy(true);
    try {
      await api.patch(`/reperes/${p.id}`, { links: [...(p.links || []), { label: form.label || form.url, url: form.url }] });
      toast.success(tf("Lien ajouté")); onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || tf("Erreur")); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent aria-describedby={undefined} className="sm:max-w-[380px]" data-testid="link-dialog">
        <DialogHeader><DialogTitle className="text-base">{tf("Ajouter un lien")} — {p.name}</DialogTitle></DialogHeader>
        <Input value={form.label} onChange={(e) => setForm((f) => ({ ...f, label: e.target.value }))}
          placeholder={tf("Libellé (ex. : Vidéo highlights)")} className="h-9 text-xs" data-testid="link-label-input" />
        <Input value={form.url} onChange={(e) => setForm((f) => ({ ...f, url: e.target.value }))}
          placeholder="https://…" className="h-9 text-xs" data-testid="link-url-input" />
        <DialogFooter>
          <Button variant="outline" size="sm" onClick={onClose}>{tf("Annuler")}</Button>
          <Button size="sm" disabled={busy || !form.url.trim()} className="bg-foreground hover:bg-foreground/90 text-background" onClick={submit} data-testid="link-save-btn">{tf("Ajouter")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function FirstContactDialog({ p, contacts, tf, onClose, onDone, onAddContact }) {
  const legalReps = useMemo(() => contacts.filter((c) => c.is_legal_representative), [contacts]);
  const options = useMemo(() => [
    ...contacts.map((c) => ({
      id: c.id, contact_id: c.id, name: c.name,
      rel: [c.relationship, c.is_legal_representative ? tf("Représentant légal") : null].filter(Boolean).join(" · "),
      phone: c.phone, email: c.email,
    })),
    { id: "self", contact_id: null, name: p.name, rel: tf("Athlète"), phone: p.phone, email: p.email },
  ], [contacts, p, tf]);
  const defaultId = p.minor && legalReps[0] ? legalReps[0].id : (p.minor && contacts[0] ? contacts[0].id : "self");
  const [recipientId, setRecipientId] = useState(defaultId);
  const recipient = options.find((o) => o.id === recipientId) || options[options.length - 1];
  const firstName = (recipient.name || "").split(" ")[0];
  const [message, setMessage] = useState(
    `${tfr("Bonjour")} ${firstName},\n\n${tfr("Je me permets de vous contacter au sujet de")} ${p.name}. ${tfr("Je serais ravi d'échanger avec vous sur son projet sportif.")}\n\n${tfr("Bien cordialement")}`);
  const channels = [
    recipient.phone && { id: "whatsapp", label: tf("Ouvrir WhatsApp"), icon: MessageCircle, href: `https://wa.me/${(recipient.phone || "").replace(/[^0-9+]/g, "")}?text=${encodeURIComponent(message)}` },
    recipient.phone && { id: "phone", label: tf("Appeler"), icon: Phone, href: `tel:${recipient.phone}` },
    recipient.email && { id: "email", label: tf("Ouvrir l'application email"), icon: Mail, href: `mailto:${recipient.email}?subject=${encodeURIComponent(`${tfr("Au sujet de")} ${p.name}`)}&body=${encodeURIComponent(message)}` },
  ].filter(Boolean);
  const [channel, setChannel] = useState(null);
  const [busy, setBusy] = useState(false);

  const changeRecipient = (id) => {
    setRecipientId(id);
    const r = options.find((o) => o.id === id);
    const fn = (r?.name || "").split(" ")[0];
    setMessage(`${tfr("Bonjour")} ${fn},\n\n${tfr("Je me permets de vous contacter au sujet de")} ${p.name}. ${tfr("Je serais ravi d'échanger avec vous sur son projet sportif.")}\n\n${tfr("Bien cordialement")}`);
    setChannel(null);
  };

  const copyMessage = () => { navigator.clipboard?.writeText(message); toast.success(tf("Message copié")); };

  const confirm = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/reperes/${p.id}/first-contact`, {
        channel: channel || (recipient.phone ? "whatsapp" : "email"),
        recipient_contact_id: recipient.contact_id, message });
      if (r.data.ok) {
        toast.success(`${p.name} ${tf("est passé de Repéré à Contacté")}`);
        onDone();
      }
    } catch (e) { toast.error(e?.response?.data?.detail || tf("Erreur")); } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent aria-describedby={undefined} className="sm:max-w-[460px] max-h-[88vh] overflow-y-auto" data-testid="first-contact-dialog">
        <DialogHeader><DialogTitle className="text-base">{tf("Premier contact")} — {p.name}</DialogTitle></DialogHeader>

        {p.minor && (
          <div className="rounded-lg border border-warning/30 bg-warning/[0.06] p-2 text-[11px] text-warning flex items-start gap-1.5" data-testid="first-contact-minor-warning">
            <ShieldAlert className="h-3.5 w-3.5 shrink-0 mt-0.5" />
            <span>
              {legalReps.length > 0
                ? `${tf("Mineur — le représentant légal est le destinataire par défaut")} : ${legalReps[0].name}`
                : tf("Aucun représentant légal renseigné.")}
              {legalReps.length === 0 && onAddContact && (
                <button onClick={onAddContact} className="underline ml-1" data-testid="first-contact-add-rep">{tf("Ajouter un représentant légal")}</button>
              )}
            </span>
          </div>
        )}

        <div>
          <label className="text-[11px] text-muted-foreground">{tf("Destinataire")}</label>
          <Select value={recipientId} onValueChange={changeRecipient}>
            <SelectTrigger className="h-9 text-xs" data-testid="first-contact-recipient"><SelectValue /></SelectTrigger>
            <SelectContent>
              {options.map((o) => (
                <SelectItem key={o.id} value={o.id}>{o.name}{o.rel ? ` — ${o.rel}` : ""}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {recipient.rel && <p className="text-[10px] text-muted-foreground mt-1">{recipient.rel}{recipient.phone ? ` · ${recipient.phone}` : ""}{recipient.email ? ` · ${recipient.email}` : ""}</p>}
        </div>

        <div>
          <label className="text-[11px] text-muted-foreground" htmlFor="fc-message">{tf("Message")}</label>
          <Textarea id="fc-message" value={message} onChange={(e) => setMessage(e.target.value)}
            className="text-xs min-h-[110px]" data-testid="first-contact-message" />
        </div>

        {channels.length === 0 ? (
          <p className="text-xs text-muted-foreground" data-testid="first-contact-no-channel">{tf("Aucune coordonnée disponible pour ce destinataire.")}</p>
        ) : (
          <div className="grid gap-1.5">
            {channels.map((c) => (
              <a key={c.id} href={c.href} target={c.id === "whatsapp" ? "_blank" : undefined} rel="noreferrer"
                onClick={() => setChannel(c.id)}
                className={cn("flex items-center gap-2 rounded-lg border px-3 py-2 text-sm transition-colors",
                  channel === c.id ? "border-athlynx/40 bg-athlynx/[0.06]" : "border-border hover:bg-secondary/50")}
                data-testid={`contact-channel-${c.id}`}>
                <c.icon className="h-4 w-4 text-athlynx" />{c.label}
                <span className="ml-auto text-[11px] text-muted-foreground">{tf("Ouvrir")}</span>
              </a>
            ))}
            <button onClick={copyMessage}
              className="flex items-center gap-2 rounded-lg border border-border px-3 py-2 text-sm hover:bg-secondary/50 transition-colors text-left"
              data-testid="first-contact-copy">
              <Copy className="h-4 w-4 text-muted-foreground" />{tf("Copier le message")}
            </button>
          </div>
        )}

        <div className="rounded-lg border border-border bg-secondary/30 p-2.5">
          <p className="text-xs font-medium">{tf("Premier contact effectué ?")}</p>
          <p className="text-[11px] text-muted-foreground mt-0.5">{tf("Ouvrir un canal ne change pas l'étape. Confirmez uniquement si le message a réellement été envoyé.")}</p>
        </div>
        <DialogFooter className="gap-1.5">
          <Button variant="outline" size="sm" onClick={onClose} data-testid="contact-not-yet">{tf("Pas encore")}</Button>
          <Button size="sm" disabled={busy || channels.length === 0} className="bg-foreground hover:bg-foreground/90 text-background"
            onClick={confirm} data-testid="contact-confirm">
            {tf("Oui, contact effectué")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
